import { useEffect, useState } from "react";
import axios from "axios";
import { TrendingUp, Wallet, Megaphone, CreditCard, Loader2 } from "lucide-react";
import { formatFCFA } from "@/lib/user";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Kpi = ({ libelle, valeur, accent }) => (
  <div className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
    <div
      className="font-heading text-2xl font-extrabold tracking-tight"
      style={{ color: accent || "#12100E" }}
    >
      {valeur}
    </div>
    <div className="text-xs font-medium text-stone-500 mt-1">{libelle}</div>
  </div>
);

const Barres = ({ serie }) => {
  const max = Math.max(...serie.map((j) => j.montant), 1);
  return (
    <div className="flex items-end gap-1.5 h-28 mt-4" data-testid="revenus-graphique">
      {serie.map((j) => (
        <div key={j.date} className="flex-1 group relative flex flex-col justify-end h-full">
          <div
            className="rounded-t-md bg-[#E05638]/85 group-hover:bg-[#E05638] transition-colors"
            style={{ height: `${Math.max((j.montant / max) * 100, j.montant ? 6 : 2)}%` }}
          />
          <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap bg-stone-900 text-white text-[10px] font-semibold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {new Date(j.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} ·{" "}
            {formatFCFA(j.montant)}
          </span>
        </div>
      ))}
    </div>
  );
};

/** Tableau de bord revenus : ce qui rentre réellement, et quelle offre vend le mieux. */
export const RevenusPanel = ({ adminId }) => {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/revenus`, { params: { admin_id: adminId } })
      .then((r) => setData(r.data))
      .catch((err) => setErreur(err.response?.status === 403
        ? "Accès réservé aux administrateurs."
        : "Revenus indisponibles pour le moment."));
  }, [adminId]);

  if (erreur) {
    return (
      <p className="mt-10 text-sm text-amber-700 font-semibold" data-testid="revenus-erreur">
        {erreur}
      </p>
    );
  }

  if (!data) {
    return (
      <div className="mt-10 flex items-center gap-2 text-sm text-stone-500">
        <Loader2 size={14} className="animate-spin" /> Chargement des revenus…
      </div>
    );
  }

  const t = data.transactions;

  return (
    <section className="mt-12" data-testid="revenus-panel">
      <h2 className="font-heading text-2xl font-extrabold text-stone-900 flex items-center gap-2">
        <TrendingUp size={22} className="text-[#0A5C36]" /> Revenus
      </h2>
      <p className="text-sm text-stone-500 mt-1">
        Encaissements réellement confirmés par l'opérateur Mobile Money.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6" data-testid="revenus-kpis">
        <Kpi libelle="Aujourd'hui" valeur={formatFCFA(data.total_jour)} accent="#0A5C36" />
        <Kpi libelle="7 derniers jours" valeur={formatFCFA(data.total_7_jours)} />
        <Kpi libelle="30 derniers jours" valeur={formatFCFA(data.total_30_jours)} />
        <Kpi libelle="Panier moyen" valeur={formatFCFA(data.panier_moyen)} />
      </div>

      <div className="mt-4 bg-white border border-[#E5DFD5] rounded-2xl p-5">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-bold text-stone-900">14 derniers jours</h3>
          <span className="text-xs text-stone-500">Total encaissé {formatFCFA(data.total_global)}</span>
        </div>
        <Barres serie={data.serie} />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className="bg-white border border-[#E5DFD5] rounded-2xl p-5">
          <h3 className="text-sm font-bold text-stone-900">Offres qui vendent le mieux</h3>
          {data.top_offres.length === 0 ? (
            <p className="text-xs text-stone-500 mt-3">Aucune vente confirmée pour le moment.</p>
          ) : (
            <ul className="mt-3 space-y-2" data-testid="revenus-top-offres">
              {data.top_offres.map((o) => (
                <li key={o.libelle} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-stone-700 capitalize truncate">{o.libelle}</span>
                  <span className="shrink-0 font-bold text-stone-900">
                    {formatFCFA(o.montant)}
                    <span className="text-stone-400 font-medium text-xs"> · {o.nb}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-[#E5DFD5] rounded-2xl p-5">
          <h3 className="text-sm font-bold text-stone-900">Parcours de paiement</h3>
          <ul className="mt-3 space-y-2 text-sm" data-testid="revenus-entonnoir">
            <li className="flex justify-between">
              <span className="text-stone-700">Paiements confirmés</span>
              <span className="font-bold text-[#0A5C36]">{t.acceptees}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-stone-700">Abandonnés en cours</span>
              <span className="font-bold text-amber-600">{t.en_attente}</span>
            </li>
            <li className="flex justify-between">
              <span className="text-stone-700">Échoués / refusés</span>
              <span className="font-bold text-red-600">{t.echouees}</span>
            </li>
            <li className="flex justify-between border-t border-[#E5DFD5] pt-2">
              <span className="text-stone-700 font-semibold">Taux de conversion</span>
              <span className="font-extrabold text-stone-900" data-testid="revenus-taux">
                {data.taux_conversion} %
              </span>
            </li>
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#FAF8F5] border border-[#E5DFD5] rounded-full px-3 py-1.5">
              <Wallet size={13} className="text-[#0A5C36]" /> Abonnements{" "}
              {formatFCFA(data.repartition.abonnements)}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#FAF8F5] border border-[#E5DFD5] rounded-full px-3 py-1.5">
              <Megaphone size={13} className="text-[#E05638]" /> Annonces{" "}
              {formatFCFA(data.repartition.annonces)}
            </span>
          </div>
        </div>
      </div>

      {data.par_operateur.length > 0 && (
        <div className="mt-4 bg-white border border-[#E5DFD5] rounded-2xl p-5">
          <h3 className="text-sm font-bold text-stone-900 flex items-center gap-2">
            <CreditCard size={15} className="text-stone-500" /> Par opérateur
          </h3>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="revenus-operateurs">
            {data.par_operateur.map((o) => (
              <li key={o.operateur} className="flex items-center justify-between text-sm">
                <span className="text-stone-700">{o.operateur}</span>
                <span className="font-bold text-stone-900">
                  {formatFCFA(o.montant)}
                  <span className="text-stone-400 font-medium text-xs"> · {o.nb}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
